import React, { useState, useCallback } from 'react';
import { View, TouchableOpacity, Text as RNText } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import Clipboard from '@react-native-clipboard/clipboard';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { TRootStackParamList } from 'app/types/navigation';
import { useNavigation } from '@react-navigation/native';
import { MainLayout } from '@shared/components/md-design-system/Layout/MainLayout';
import { Text } from '@shared/components/text';
import { Head } from '@shared/components/head';
import Button from '@shared/components/md-design-system/Button/Button';
import { Loading } from '@shared/components/loading';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { useUploadSession, useCountdown } from 'app/_modules/history';

type Props = NativeStackScreenProps<TRootStackParamList, 'DocumentUploadQRPage'>;

export const DocumentUploadQR = (props: Props) => {
  const { pathwayId } = props.route.params;
  const navigation = useNavigation();
  const { t } = dynamicTranslations();
  const s = 'history.documents.patientSection.uploadQR';

  const { session, isLoading, error, refresh } = useUploadSession(pathwayId);
  const { timeLeft, isExpired } = useCountdown(session?.expires_at);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!session) return;
    Clipboard.setString(session.upload_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [session]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <View className="flex-1 justify-center items-center py-16">
          <Loading message={t(`${s}.loading`)} />
        </View>
      );
    }

    if (error) {
      return (
        <View className="flex-1 justify-center items-center py-16 gap-4">
          <Text className="text-sm text-gray-600 text-center">{t(`${s}.error`)}</Text>
          <Button variant="primary" onPress={refresh}>
            {t(`${s}.retry`)}
          </Button>
        </View>
      );
    }

    if (!session) return null;

    if (isExpired) {
      return (
        <View className="flex-1 justify-center items-center py-16 gap-4">
          <Head type="h2" className="text-gray-900 text-center">
            {t(`${s}.expired`)}
          </Head>
          <Text className="text-sm text-gray-600 text-center">
            {t(`${s}.expiredMessage`)}
          </Text>
          <Button variant="primary" onPress={refresh}>
            {t(`${s}.regenerate`)}
          </Button>
        </View>
      );
    }

    return (
      <>
        <View className="self-start bg-primary-50 border border-primary-200 rounded-full px-3 py-1 mb-6">
          <Text className="text-xs font-semibold text-primary-700">
            {t(`${s}.expiresIn`, { time: timeLeft })}
          </Text>
        </View>

        <Head type="h3" className="text-gray-900 mb-2">
          {t(`${s}.method1Title`)}
        </Head>
        <Text className="text-sm text-gray-700 mb-5">
          {t(`${s}.method1Instructions`)}
        </Text>

        <View className="self-center mb-8 bg-white rounded-xl border border-gray-200 p-4">
          <QRCode value={session.upload_url} size={200} />
        </View>

        <View className="flex-row items-center mb-8 gap-3">
          <View className="flex-1 h-px bg-gray-200" />
          <Text className="text-sm text-gray-500">{t(`${s}.or`)}</Text>
          <View className="flex-1 h-px bg-gray-200" />
        </View>

        <Head type="h3" className="text-gray-900 mb-4">
          {t(`${s}.method2Title`)}
        </Head>

        <View className="flex-row items-center justify-between border border-gray-200 rounded-xl p-3 mb-3">
          <RNText className="text-sm text-gray-700 flex-1 mr-2" numberOfLines={3}>
            {session.upload_url}
          </RNText>
          <TouchableOpacity
            onPress={handleCopy}
            className="bg-primary-50 border border-primary-200 rounded-lg px-3 py-1.5"
            activeOpacity={0.7}
          >
            <Text className="text-xs font-semibold text-primary-700">
              {copied ? t(`${s}.copied`) : t(`${s}.copy`)}
            </Text>
          </TouchableOpacity>
        </View>

        <View className="flex-row items-center gap-2">
          <Text className="text-sm text-gray-700">{t(`${s}.sessionCode`)}</Text>
          <Text className="text-base font-bold text-gray-900 tracking-widest">
            {session.token}
          </Text>
        </View>
      </>
    );
  };

  return (
    <MainLayout navigation={() => navigation.goBack()} scrollable>
      <Head type="h1" className="text-primary-900 mb-3 mt-4">
        {t(`${s}.title`)}
      </Head>
      <Text className="text-sm text-gray-700 mb-8">
        {t(`${s}.subtitle`)}
      </Text>

      {renderContent()}
    </MainLayout>
  );
};
